import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../design/ui';
import { displayLocale } from '../utils/dates';
import './DashboardChanges.css';

// « Cette semaine »: tickets created over the last 7 days per carrier, client
// and motif, against the 7 days before — GET /api/tickets/dashboard-insights
// `breakdowns` ({label, current, previous, delta_pct}, top by volume). A rise
// is flagged only when it is both large and real (RISE_PCT and RISE_MIN
// tickets): +200 % on 1 → 3 tickets is noise, not news.

const RISE_PCT = 30;
const RISE_MIN = 5;
const ROWS = 5;

export const changeOf = ({ current = 0, previous = 0 }) => {
  if (previous === 0) return current > 0 ? { kind: 'new' } : { kind: 'flat' };
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct >= RISE_PCT && current - previous >= RISE_MIN) return { kind: 'rise', pct };
  if (pct <= -RISE_PCT && previous - current >= RISE_MIN) return { kind: 'drop', pct };
  return { kind: pct === 0 ? 'flat' : 'steady', pct };
};

const DIMENSIONS = [
  ['carriers', 'Transporteurs'],
  ['clients', 'Clients'],
  ['topics', 'Motifs'],
];

const DashboardChanges = ({ breakdowns }) => {
  const { t, i18n } = useTranslation();
  const number = (value) => new Intl.NumberFormat(displayLocale(i18n.language)).format(value).replace(/\u202f/g, '\u00a0');
  if (!breakdowns) return null;

  // A dimension with fewer than two entries compares nothing (one shop on a
  // single-store account): it is left out rather than shown as a list of one.
  const columns = DIMENSIONS
    .map(([key, title]) => ({ key, title, rows: (breakdowns[key] || []).slice(0, ROWS) }))
    .filter((column) => column.rows.length >= 2);
  if (columns.length === 0) return null;

  const badgeFor = (change) => {
    if (change.kind === 'new') return <Badge tone="info">{t('dashChangesUi.new', 'nouveau')}</Badge>;
    if (change.kind === 'rise') return <Badge tone="warning">{`+${change.pct} %`}</Badge>;
    if (change.kind === 'drop') return <Badge tone="success">{`${change.pct} %`}</Badge>;
    if (change.kind === 'steady') {
      return <span className="dash-changes__pct">{`${change.pct > 0 ? '+' : ''}${change.pct} %`}</span>;
    }
    return <span className="dash-changes__pct">=</span>;
  };

  return (
    <div className="dash-changes">
      <p className="dash-changes__scope">
        {t('dashChangesUi.scope', 'Tickets créés ces 7 derniers jours, comparés aux 7 jours précédents.')}
      </p>
      <div className="dash-changes__columns">
        {columns.map((column) => (
          <section key={column.key} className="dash-changes__column" aria-label={t(`dashChangesUi.${column.key}`, column.title)}>
            <span className="u-overline">{t(`dashChangesUi.${column.key}`, column.title)}</span>
            <ul className="dash-changes__list">
              {column.rows.map((row) => {
                const change = changeOf(row);
                return (
                  <li key={row.label} className="dash-changes__row">
                    <span className="dash-changes__label" title={row.label}>{row.label}</span>
                    <span className="dash-changes__count"
                      title={t('dashChangesUi.previous', '{{previous}} les 7 jours précédents', { previous: number(row.previous || 0) })}>
                      {number(row.current || 0)}
                    </span>
                    <span className="dash-changes__delta">{badgeFor(change)}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
};

export default DashboardChanges;
